/**
 * LIFF Service
 * Handles LINE Front-end Framework integration
 */

import liff from '@line/liff';
import { getLiffId } from './config';
import type { UserProfile } from './types';

// Store user profile
let userProfile: UserProfile | null = null;

export function setUserProfile(profile: UserProfile | null): void {
  userProfile = profile;
}

// Demo mode flag
let isDemoMode = false;

/**
 * Check if in demo mode
 */
export function isDemo(): boolean {
  return isDemoMode;
}

/**
 * Initialize LIFF SDK
 * Resolves LIFF ID at runtime from tenant config API (SaaS-ready).
 */
export async function initLiff(): Promise<void> {
  // Resolve LIFF ID dynamically (API → env fallback)
  const liffId = await getLiffId();

  if (!liffId || liffId === 'your-liff-id-here' || liffId.length < 10) {
    console.warn('LIFF ID not configured, running in demo mode');
    isDemoMode = true;
    return;
  }

  try {
    await liff.init({ liffId });
    console.log('LIFF initialized successfully (liffId:', liffId, ')');
  } catch (error) {
    console.warn('LIFF initialization failed, falling back to demo mode:', error);
    isDemoMode = true;
  }
}

/**
 * Check if running in LINE app
 */
export function isInLineApp(): boolean {
  if (isDemoMode) return false;
  if (typeof navigator !== 'undefined' && /Line\//i.test(navigator.userAgent)) {
    return true;
  }
  try {
    return liff.isInClient();
  } catch (_) {
    return false;
  }
}

/**
 * Check if user is logged in
 */
export function isLoggedIn(): boolean {
  if (isDemoMode) return true;
  try {
    return liff.isLoggedIn();
  } catch (_) {
    return false;
  }
}

/**
 * Login user (preserves exact current URL and query parameters)
 */
export function login(customRedirectUri?: string): void {
  if (isDemoMode) return;
  if (!isLoggedIn()) {
    const redirectUri = customRedirectUri || (typeof window !== 'undefined' ? window.location.href : undefined);
    try {
      if (redirectUri) {
        liff.login({ redirectUri });
      } else {
        liff.login();
      }
    } catch (e) {
      console.warn('[LIFF] login error:', e);
    }
  }
}

/**
 * Logout user
 */
export function logout(): void {
  if (isDemoMode) return;
  if (isLoggedIn()) {
    liff.logout();
  }
}

/**
 * Get user profile
 */
export async function getUserProfile(): Promise<UserProfile> {
  // Demo mode - return fake profile with consistent ID
  if (isDemoMode) {
    return {
      userId: 'demo-user-001',
      displayName: 'Demo User',
      language: navigator.language || 'th',
    };
  }

  if (userProfile) {
    return userProfile;
  }

  try {
    if (liff && typeof liff.getProfile === 'function') {
      const profile = await liff.getProfile();
      const language = (typeof liff.getLanguage === 'function' ? liff.getLanguage() : null) || 'th';

      userProfile = {
        userId: profile.userId,
        displayName: profile.displayName,
        pictureUrl: profile.pictureUrl,
        language,
      };

      return userProfile;
    }
  } catch (error) {
    console.warn('[LIFF] Failed to get user profile from LIFF SDK, falling back:', error);
  }

  // Fallback to store user if available
  try {
    const { getState } = await import('./store');
    const storeUser = getState()?.user;
    if (storeUser && storeUser.userId) {
      userProfile = storeUser;
      return userProfile;
    }
  } catch (_) {}

  // Last-resort fallback for guest/unauthenticated session
  const fallbackGuestId = typeof localStorage !== 'undefined'
    ? (localStorage.getItem('revio_web_user_id') || `guest_${Date.now()}`)
    : `guest_${Date.now()}`;
  return {
    userId: fallbackGuestId,
    displayName: 'Guest',
    language: 'th',
  };
}

/**
 * Alias for getUserProfile (for consistency)
 */
export const getProfile = getUserProfile;

/**
 * Get LIFF language setting
 */
export function getLiffLanguage(): string {
  if (isDemoMode) return navigator.language || 'th';
  return liff.getLanguage() || 'th';
}

/**
 * Close LIFF window (return to LINE chat)
 */
export function closeWindow(): void {
  // 1. WeChat Webview native close
  if (typeof (window as any).WeixinJSBridge !== 'undefined' && typeof (window as any).WeixinJSBridge.call === 'function') {
    (window as any).WeixinJSBridge.call('closeWindow');
    return;
  }

  // 2. LINE LIFF native close
  if (isInLineApp()) {
    try {
      liff.closeWindow();
      return;
    } catch (_) {}
  }

  // 3. Browser fallback
  try {
    window.close();
  } catch (_) {}
  if (typeof history !== 'undefined' && history.length > 1) {
    history.back();
  }
}

/**
 * Send message to LINE chat
 */
export async function sendMessage(message: string | Record<string, any>): Promise<void> {
  console.log('[LIFF] sendMessage called');
  console.log('[LIFF] isDemoMode:', isDemoMode);
  console.log('[LIFF] isInLineApp:', isInLineApp());
  console.log('[LIFF] Message:', message);

  if (isDemoMode) {
    console.warn('[LIFF] Running in demo mode, message not sent to LINE chat:', message);
    return;
  }

  if (!isInLineApp()) {
    console.warn('[LIFF] Not in LINE app client, skipping liff.sendMessages');
    return;
  }

  try {
    console.log('[LIFF] Sending message via liff.sendMessages...');
    if (typeof message === 'string') {
      await liff.sendMessages([{ type: 'text', text: message }]);
    } else {
      await liff.sendMessages([message as any]);
    }
    console.log('[LIFF] Message sent successfully');
  } catch (error) {
    console.error('[LIFF] Failed to send message via liff.sendMessages:', error);
  }
}

/**
 * Share target picker (share to friends/groups)
 */
export async function shareMessage(text: string): Promise<void> {
  if (isDemoMode) {
    console.log('[Demo] Would share message:', text);
    return;
  }

  if (!liff.isApiAvailable('shareTargetPicker')) {
    console.log('Share target picker is not available');
    return;
  }

  try {
    await liff.shareTargetPicker([
      {
        type: 'text',
        text,
      },
    ]);
  } catch (error) {
    console.error('Failed to share message:', error);
  }
}

/**
 * Get access token for API calls
 */
export function getAccessToken(): string | null {
  if (isDemoMode) return 'demo-token';
  return liff.getAccessToken();
}

/**
 * Open external URL
 */
export function openUrl(url: string, external: boolean = false): void {
  if (external) {
    liff.openWindow({ url, external: true });
  } else {
    window.location.href = url;
  }
}
